import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Accept, DropzoneInputProps, DropzoneRootProps, FileRejection, useDropzone } from "react-dropzone";
import { Box, Flex, FlexProps, Icon, Spinner, Text, createIcon } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { InvoiceAttachment } from "@/types";
import { createFileErrorMessage, formatBytes } from "@/utils/fileUtils";
import { TruncatedText } from "./TruncatedText";
import { Link, Button, ErrorMessage, BodyShort, Heading } from "@digital-services-gard-as/anchor-react";
import { TrashIcon } from "@digital-services-gard-as/anchor-icons";

type FileInputPropsBase = {
    id?: string;
    maxSize?: number;
    name: string;
    accept: Accept | undefined;
    isInvalid: boolean;
    isLoadingOcr?: boolean;
    dropzoneStyles?: FlexProps;
    onDeleteClick?: () => void;
    onUpload?: (file: File) => void;
};

type FileInputPropsWithAttachmentClick = FileInputPropsBase & {
    onAttachmentClick: (invoiceId: string, docId: string | undefined) => void;
    invoiceId: string;
};

type FileInputPropsWithoutAttachmentClick = FileInputPropsBase & {
    onAttachmentClick?: never;
    invoiceId?: never;
};

type FileInputProps = FileInputPropsWithAttachmentClick | FileInputPropsWithoutAttachmentClick;

const InvoiceFileUpload = ({
    accept,
    name,
    onUpload,
    onDeleteClick,
    onAttachmentClick,
    id,
    invoiceId,
    isInvalid,
    isLoadingOcr = false,
    dropzoneStyles,
    maxSize = DEFAULT_MAX_FILE_SIZE
}: FileInputProps) => {
    const [fileRejection, setFileRejection] = useState<FileRejection>({} as FileRejection);
    const { register, unregister, setValue, watch } = useFormContext();
    const attachment: InvoiceAttachment = watch(name);

    const onDrop = useCallback(
        (acceptedFiles: File[], rejections: FileRejection[]) => {
            if (acceptedFiles.length >= 1) {
                setFileRejection({} as FileRejection);
                const files: InvoiceAttachment[] = acceptedFiles.map(file => {
                    return {
                        file,
                        filename: file.name.split(".")[0],
                        fileSize: file.size,
                        fileExtension: file.name.split(".")[1],
                        isUploaded: false,
                        isUploading: false,
                        uploadFailed: false
                    };
                });
                const firstAttachment = files[0];
                if (firstAttachment) {
                    setValue(name, firstAttachment, { shouldValidate: true });
                    const droppedFile = firstAttachment.file;
                    if (droppedFile) {
                        onUpload?.(droppedFile);
                    }
                }
                return;
            }

            if (rejections.length >= 1) {
                setFileRejection(rejections[0]);
                setValue(name, undefined);
                return;
            }

            setFileRejection({} as FileRejection);
        },
        [setValue, name, onUpload]
    );

    const dropzoneState = getDropzoneState(attachment, isLoadingOcr, fileRejection);
    const isDisabled = dropzoneState === "uploading" || dropzoneState === "reading" || dropzoneState === "uploaded";

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept,
        maxSize,
        multiple: false,
        disabled: isDisabled
    });

    useEffect(() => {
        register(name, { value: attachment });
        return () => {
            unregister(name);
        };
    }, [register, unregister, name, attachment]);

    const handleAttachmentClick = useCallback(() => {
        if (!onAttachmentClick || !invoiceId) {
            return;
        }
        onAttachmentClick(invoiceId, attachment?.docId);
    }, [onAttachmentClick, invoiceId, attachment]);

    return (
        <Dropzone
            state={dropzoneState}
            isInvalid={isInvalid}
            id={id}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
            dropzoneStyles={dropzoneStyles}
            maxSize={maxSize}
            attachment={attachment}
            fileRejection={fileRejection}
            accept={accept}
            onDeleteClick={onDeleteClick}
            onAttachmentClick={handleAttachmentClick}
        />
    );
};

type DropzoneState = "ready" | "uploading" | "reading" | "error" | "uploaded";

// eslint-disable-next-line no-magic-numbers
const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024;
const CLICK_TO_UPLOAD_KEY = "createInvoice.fileUpload.clickToUpload";

const getDropzoneState = (
    attachment: InvoiceAttachment | undefined,
    isLoadingOcr: boolean,
    fileRejection: FileRejection
): DropzoneState => {
    if (attachment?.uploadFailed) {
        return "error";
    }
    if (attachment?.isUploaded && !isLoadingOcr && !attachment.isUploading) {
        return "uploaded";
    }
    if (isLoadingOcr) {
        return "reading";
    }
    if (attachment?.isUploading) {
        return "uploading";
    }
    if (fileRejection?.errors?.length) {
        return "error";
    }
    return "ready";
};

type DropzoneProps = {
    state: DropzoneState;
    isInvalid: boolean;
    getRootProps: <T extends DropzoneRootProps>(props?: T | undefined) => T;
    getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T;
    isDragActive: boolean;
    id?: string;
    dropzoneStyles?: FlexProps;
    maxSize: number;
    attachment: InvoiceAttachment | undefined;
    fileRejection: FileRejection;
    accept: Accept | undefined;
    onDeleteClick?: () => void;
    onAttachmentClick: () => void;
};

const Dropzone = ({
    state,
    isInvalid,
    getRootProps,
    getInputProps,
    isDragActive,
    id,
    dropzoneStyles,
    maxSize,
    attachment,
    fileRejection,
    accept,
    onDeleteClick,
    onAttachmentClick
}: DropzoneProps) => {
    const isInteractive = state === "ready" || state === "error";
    const showErrorBorder = isInvalid || state === "error";

    useEffect(() => {
        if (!isInvalid || !id) {
            return;
        }

        const input = document.getElementById(id);
        input?.focus();
    }, [isInvalid, id]);

    return (
        <Flex
            {...(isInteractive ? getRootProps() : {})}
            id="fileUpload"
            backgroundColor="anchor.gray.100"
            flexDir="column"
            justifyContent="center"
            alignItems="center"
            padding="1.5rem"
            gap="0.75rem"
            w="100%"
            borderRadius="0.5rem"
            border={showErrorBorder ? "1px solid red" : {}}
            cursor={isInteractive ? "pointer" : "default"}
            {...dropzoneStyles}
        >
            {isInteractive && <input id={id} {...getInputProps()} type="file" multiple={false} />}
            <DropzoneContent
                state={state}
                isDragActive={isDragActive}
                maxSize={maxSize}
                attachment={attachment}
                fileRejection={fileRejection}
                accept={accept}
                onDeleteClick={onDeleteClick}
                onAttachmentClick={onAttachmentClick}
            />
        </Flex>
    );
};

const ClickToUploadLink = () => {
    const { t } = useTranslation("invoices");

    return (
        <Text as="span" color="anchor.navy.900" fontSize="1.125rem" fontWeight="700" textDecoration="underline">
            {t(CLICK_TO_UPLOAD_KEY)}
        </Text>
    );
};

type ReadyDropzoneTextProps = {
    isDragActive: boolean;
    maxSize: number;
};

const ReadyDropzoneText = ({ isDragActive, maxSize }: ReadyDropzoneTextProps) => {
    const { t } = useTranslation("invoices");

    if (isDragActive) {
        return <p>{t("createInvoice.fileUpload.dropHere")}</p>;
    }

    return (
        <Box textAlign="center">
            <Heading size="md" level="3" align="center" data-testid="dropzoneReadyText">
                {t("createInvoice.fileUpload.dropInvoicesHere")}
            </Heading>
            <BodyShort size="lg" as="span" color="anchor.navy.900">
                {t("createInvoice.fileUpload.or")} <ClickToUploadLink />
            </BodyShort>
            <BodyShort size="sm">
                {t("createInvoice.fileUpload.allowedFiletypes", {
                    maxSize: formatBytes(maxSize)
                })}
            </BodyShort>
        </Box>
    );
};

const DropzoneError = ({
    attachment,
    fileRejection,
    accept
}: {
    attachment: InvoiceAttachment | undefined;
    fileRejection: FileRejection;
    accept: Accept | undefined;
}) => {
    const { t } = useTranslation("errorMessages");

    if (fileRejection?.errors?.length) {
        return (
            <>
                {fileRejection.errors.map(error => (
                    <ErrorMessage key={error.code} id="fileError" data-testid="fileError">
                        {createFileErrorMessage(fileRejection.file.name, error.code, accept)}
                    </ErrorMessage>
                ))}
            </>
        );
    }

    if (attachment?.uploadFailed) {
        return (
            <ErrorMessage id="uploadFailed">
                {t("invoiceForm.fileUploader.uploadFailed", {
                    attachment: `${attachment.filename}.${attachment.fileExtension}`,
                    attachmentSize: formatBytes(attachment.fileSize)
                })}
            </ErrorMessage>
        );
    }

    return null;
};

type LoadingDropzoneContentProps = {
    state: "uploading" | "reading";
};

const LoadingDropzoneContent = ({ state }: LoadingDropzoneContentProps) => {
    const { t } = useTranslation("invoices");
    const isUploading = state === "uploading";

    return (
        <>
            <Spinner
                borderWidth="4px"
                animationDuration="0.65s"
                style={{ "--spinner-track-color": "var(--chakra-colors-gray-200)" } as React.CSSProperties}
                color="anchor.navy.800"
                size="md"
            />
            <Box textAlign="center">
                <Heading
                    size="md"
                    level="3"
                    align="center"
                    data-testid={isUploading ? "dropzoneUploading" : "dropzoneReading"}
                >
                    {t(
                        isUploading
                            ? "createInvoice.fileUpload.uploadingTitle"
                            : "createInvoice.fileUpload.readingTitle"
                    )}
                </Heading>
                <BodyShort size="sm" color="anchor.gray.600">
                    {t(
                        isUploading
                            ? "createInvoice.fileUpload.uploadingSubtitle"
                            : "createInvoice.fileUpload.readingSubtitle"
                    )}
                </BodyShort>
            </Box>
        </>
    );
};

type DropzoneContentProps = {
    state: DropzoneState;
    isDragActive: boolean;
    maxSize: number;
    attachment: InvoiceAttachment | undefined;
    fileRejection: FileRejection;
    accept: Accept | undefined;
    onDeleteClick?: () => void;
    onAttachmentClick: () => void;
};

const DropzoneContent = ({
    state,
    isDragActive,
    maxSize,
    attachment,
    fileRejection,
    accept,
    onDeleteClick,
    onAttachmentClick
}: DropzoneContentProps) => {
    if (state === "uploading" || state === "reading") {
        return <LoadingDropzoneContent state={state} />;
    }

    if (state === "uploaded" && attachment) {
        return (
            <Flex
                backgroundColor="white"
                alignItems="center"
                justify="space-between"
                p="1.2rem"
                borderRadius="0.5rem"
                border="1px"
                borderColor="anchor.brown.200"
                w="100%"
            >
                <Flex alignItems="center">
                    <Link id="attachmentLink" onClick={onAttachmentClick}>
                        <Box display="flex" flexDir="row" gap="0.2rem" alignItems="center">
                            <Box color="blue" asChild>
                                <u>
                                    <TruncatedText text={`${attachment.filename}.${attachment.fileExtension}`} />
                                </u>
                            </Box>
                            <Box>({formatBytes(attachment.fileSize)})</Box>
                        </Box>
                    </Link>
                </Flex>
                <Button
                    id="deleteAttachmentBtn"
                    variant="tertiary"
                    onClick={onDeleteClick}
                    icon={<TrashIcon />}
                    style={{ fontSize: "1.2rem", color: "blue" }}
                />
            </Flex>
        );
    }

    if (state === "ready" || state === "error") {
        return (
            <>
                <Icon color="anchor.navy.800" textAlign="center" boxSize="48px" asChild>
                    <CloudUploadIcon />
                </Icon>
                <ReadyDropzoneText isDragActive={isDragActive} maxSize={maxSize} />
                {!isDragActive && (
                    <DropzoneError attachment={attachment} fileRejection={fileRejection} accept={accept} />
                )}
            </>
        );
    }

    return null;
};

export default InvoiceFileUpload;

//Switch this icon with anchor icon when anchor icon is provided
const CloudUploadIcon = createIcon({
    displayName: "CloudUploadIcon",
    viewBox: "0 0 48 48",
    defaultProps: {
        fill: "currentColor"
    },
    path: (
        <>
            <g clipPath="url(#cloudUploadClip)">
                <path
                    // eslint-disable-next-line max-len
                    d="M23.8489 22.507C23.9483 22.4971 24.0485 22.4971 24.1478 22.507C24.1639 22.5086 24.1797 22.5117 24.1956 22.5138C24.2276 22.518 24.2596 22.5222 24.2913 22.5285C24.3134 22.5328 24.335 22.5388 24.3568 22.5441C24.3839 22.5507 24.411 22.5574 24.4378 22.5656C24.4493 22.5691 24.4606 22.5735 24.472 22.5773C24.5512 22.6036 24.6287 22.6361 24.7034 22.6759C24.7245 22.6871 24.7446 22.6999 24.765 22.7121C24.7875 22.7255 24.8104 22.7384 24.8323 22.7531C24.8389 22.7575 24.8453 22.7622 24.8519 22.7667C24.9248 22.8173 24.9949 22.8746 25.0599 22.9396L33.0599 30.9396C33.6454 31.5254 33.6455 32.475 33.0599 33.0607C32.4741 33.6465 31.5236 33.6465 30.9378 33.0607L25.4984 27.6203V42.0001C25.4983 42.8285 24.8267 43.5001 23.9984 43.5001C23.17 43.5001 22.4984 42.8285 22.4984 42.0001V27.6222L17.0599 33.0607C16.4741 33.6465 15.5236 33.6465 14.9378 33.0607C14.3524 32.475 14.3525 31.5253 14.9378 30.9396L22.9378 22.9396C22.9883 22.8891 23.0426 22.8437 23.098 22.8019C23.1214 22.7843 23.1439 22.7654 23.1683 22.7492C23.1784 22.7424 23.1893 22.7371 23.1995 22.7306C23.227 22.7133 23.2549 22.6964 23.2835 22.6808L23.3011 22.672C23.3132 22.6656 23.3259 22.6605 23.3382 22.6544C23.366 22.6408 23.3935 22.6264 23.4222 22.6144C23.4475 22.6039 23.4736 22.5952 23.4993 22.5861C23.5169 22.5799 23.5342 22.5731 23.5521 22.5675L23.5716 22.5617C23.5887 22.5566 23.6061 22.5524 23.6234 22.548C23.6511 22.5409 23.679 22.534 23.7073 22.5285C23.709 22.5281 23.7106 22.5278 23.7122 22.5275L23.845 22.508C23.8464 22.5078 23.8476 22.5071 23.8489 22.507ZM17.3431 4.51577C19.9864 4.41839 22.6175 4.92167 25.0384 5.98745C27.4594 7.05329 29.6073 8.65463 31.3206 10.6701C32.7802 12.3871 33.8923 14.3669 34.5989 16.4992H35.9984C38.5531 16.5009 41.0355 17.3534 43.0521 18.922C45.0687 20.4907 46.5058 22.6863 47.1361 25.1623C47.7663 27.6383 47.5538 30.2539 46.5325 32.5958C45.5113 34.9377 43.7395 36.8729 41.4964 38.0958C40.7691 38.4924 39.8578 38.2244 39.4613 37.4972C39.0647 36.7699 39.3326 35.8586 40.0599 35.4621C41.718 34.5581 43.0277 33.1277 43.7825 31.3966C44.5374 29.6656 44.6946 27.7326 44.2288 25.9025C43.763 24.0724 42.7008 22.4497 41.2103 21.2902C39.7198 20.1307 37.8857 19.5005 35.9974 19.4992H33.4779C32.7941 19.4991 32.1969 19.0362 32.0257 18.3742C31.4771 16.2524 30.4549 14.2823 29.0355 12.6124C27.6159 10.9425 25.8354 9.61667 23.8294 8.73354C21.8235 7.85048 19.6436 7.43312 17.4534 7.51381C15.2632 7.59452 13.1194 8.17102 11.1839 9.19936C9.24848 10.2277 7.5714 11.6815 6.27863 13.4513C4.98591 15.2211 4.11104 17.2607 3.72004 19.4171C3.32907 21.5738 3.43156 23.7914 4.02082 25.9025C4.61008 28.0136 5.67101 29.9632 7.12238 31.6056C7.67083 32.2263 7.61201 33.1742 6.99152 33.7228C6.37076 34.2714 5.42291 34.2127 4.87434 33.5919C3.1228 31.6099 1.84333 29.2568 1.13215 26.7091C0.420975 24.1613 0.296025 21.4848 0.767891 18.882C1.23977 16.2794 2.29564 13.8177 3.85578 11.6818C5.4161 9.54574 7.44067 7.79106 9.77668 6.54995C12.1127 5.30884 14.6996 4.61316 17.3431 4.51577Z"
                />
            </g>
            <defs>
                <clipPath id="cloudUploadClip">
                    <rect width="48" height="48" fill="white" />
                </clipPath>
            </defs>
        </>
    )
});
